import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaImages, FaSave, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";

import AdminDrawer from "./AdminDrawer";

import {
  emptyAlbumForm,
  mapAlbumToForm,
} from "../utils/albumsPhotosUtils";

export default function AlbumFormDrawer({
  isOpen,
  mode = "create",
  album,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(emptyAlbumForm);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && album) {
      setFormData(mapAlbumToForm(album));
    } else {
      setFormData(emptyAlbumForm);
    }
  }, [isOpen, isEdit, album]);

  const title = isEdit ? "Modifier un album" : "Créer un album";

  const subtitle = isEdit
    ? "Modifiez le titre, la description et la visibilité de l’album."
    : "Créez un album qui regroupera ensuite plusieurs photos.";

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.titre.trim()) {
      toast.error("Le titre de l'album est obligatoire.");
      return;
    }

    const payload = {
      titre: formData.titre,
      description: formData.description,
      is_active: Boolean(formData.is_active),
    };

    if (isEdit) {
      await onUpdate?.(album.id, payload);
    } else {
      await onCreate?.(payload);
    }
  };

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <FaImages />
              </span>

              <div>
                <h3 className="font-bold text-slate-950 dark:text-white">
                  Informations de l’album
                </h3>

                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Ces informations seront visibles sur la page publique.
                </p>
              </div>
            </div>

            <div className="grid gap-4">
              <InputField
                label="Titre de l'album"
                name="titre"
                value={formData.titre}
                onChange={handleChange}
                required
              />

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Description
                </label>

                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Description de l'album..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-white/10">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={Boolean(formData.is_active)}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />

                <span className="inline-flex items-center gap-2">
                  <FaCheckCircle
                    className={
                      formData.is_active ? "text-emerald-500" : "text-slate-400"
                    }
                  />
                  Publier l’album sur le site
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving
              ? "Enregistrement..."
              : isEdit
                ? "Mettre à jour"
                : "Créer"}
          </button>
        </div>
      </form>
    </AdminDrawer>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}